import moment from 'moment'

type DateLike = Date | string | null | undefined

/** Divider label used when an item has no usable date at all. */
export const UNDATED_GROUP_LABEL = `Undated`

function toIso(value: DateLike): string | undefined {
  if (!value) return undefined
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? undefined : value.toISOString()
  const parsed = moment.utc(value)
  return parsed.isValid() ? parsed.toISOString() : undefined
}

/**
 * The date an activity should be filed under in the feed. `activityDate` is
 * nullable in the API (AI Workflow and some Call activities arrive without
 * one), so fall back to `createdDate`, which is always present.
 */
export function activityFeedDate(activity: { activityDate?: DateLike; createdDate?: DateLike }): string | undefined {
  return toIso(activity.activityDate) ?? toIso(activity.createdDate)
}

/**
 * Month divider label for a feed group, e.g. "August 2026". Never returns
 * moment's "Invalid date" string for missing or unparseable input.
 */
export function monthGroupLabel(date: DateLike): string {
  const iso = toIso(date)
  if (!iso) return UNDATED_GROUP_LABEL
  return moment.utc(iso).startOf(`month`).format(`MMMM YYYY`)
}

/** Sorts feed items newest first, keeping undated items at the end. */
export function compareFeedDatesDesc(a: string | undefined, b: string | undefined): number {
  if (a && b) return b.localeCompare(a)
  if (a) return -1
  if (b) return 1
  return 0
}

/**
 * The instant an appointment sorts by. The API keeps the calendar day in `date`
 * and the wall-clock time in a separate `time` string ("HH:mm"), so a query
 * sorted on `date` alone leaves same-day appointments in arbitrary index order.
 * Appointments with no usable time fall back to the day itself, which puts them
 * last within their day under a newest-first sort.
 */
export function appointmentFeedDate(appointment: { date?: DateLike; time?: string | null }): string | undefined {
  const iso = toIso(appointment.date)
  if (!iso) return undefined
  const [hour, minute] = (appointment.time || ``).split(`:`)
  const wallClock = moment.utc(`${moment.utc(iso).format(`YYYY-MM-DD`)}T${hour}:${minute}`, `YYYY-MM-DDTHH:mm`, true)
  return wallClock.isValid() ? wallClock.toISOString() : iso
}
